// src/pages/CustomersPage.js
import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LoadingIndicator from "../components/LoadingIndicator";
import { FaChevronDown, FaPlus, FaTimes, FaTrash } from "react-icons/fa";
import {
  collection,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCompany } from "../contexts/CompanyContext";

// Stilkonstanter
const boxStyle = {
  position: "relative",
  display: "inline-block",
  flex: "0 1 220px",
  minWidth: 150,
  maxWidth: 260,
};

const inputStyle = {
  width: "100%",
  height: 50,
  padding: "8px 34px 8px 12px",
  border: "1px solid #d1d5db",
  borderRadius: 3,
  background: "#ffffff",
  fontSize: 14,
  color: "#111827",
  outline: "none",
  boxSizing: "border-box",
};

const labelStyle = {
  position: "absolute",
  top: -10,
  left: 12,
  fontSize: "0.7rem",
  background: "#ffffff",
  padding: "0 4px",
  color: "#6b7280",
  userSelect: "none",
};

const toolbarButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 42,
  height: 42,
  borderRadius: 8,
  color: "white",
  fontSize: 18,
  padding: 0,
  boxSizing: "border-box",
  cursor: "pointer",
};

// Översättning av kundtyper
const CUSTOMER_TYPE_LABELS_SV = {
  company: "Företag",
  organization: "Organisation",
  individual: "Privatperson",
};

// Hjälpfunktion för att få aktuellt år
const getCurrentYear = () => {
  return new Date().getFullYear().toString();
};

// Hjälpfunktion för att få aktuell månad på svenska
const getCurrentMonthInSwedish = () => {
  const months = [
    "Januari", "Februari", "Mars", "April", "Maj", "Juni",
    "Juli", "Augusti", "September", "Oktober", "November", "December"
  ];
  const currentMonthIndex = new Date().getMonth();
  return months[currentMonthIndex];
};

// Huvudkomponent för kundhantering
export default function CustomersPage() {
  const navigate = useNavigate();
  const { currentUser, profile } = useAuth();
  const { activeCompany, loading: companyLoading } = useCompany();

  // State för filtrering och sökning
  const [searchTerm, setSearchTerm] = useState("");
  const [yearFilter, setYearFilter] = useState(getCurrentYear());
  const [monthFilter, setMonthFilter] = useState(getCurrentMonthInSwedish());
  
  // State för popup-fönster
  const [showAddPopup, setShowAddPopup] = useState(false);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // State för data
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingError, setSavingError] = useState("");

  // State för ny kund
  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    organizationNumber: "",
    contactPerson: "",
    customerType: "",
  });

  // Hämta companyAccountUid från activeCompany
  const companyAccountUid = activeCompany?.companyAccountUid;

  // Hämta kunder från Firestore - använder companyAccountUid
  useEffect(() => {
    if (!companyAccountUid) {
      setCustomers([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    console.log("Hämtar kunder för företag:", activeCompany?.companyName);
    console.log("Company Account UID:", companyAccountUid);

    const customersRef = collection(db, "users", companyAccountUid, "customers");

    const unsubscribe = onSnapshot(
      customersRef,
      (snapshot) => {
        const customerList = [];

        snapshot.forEach((customerDoc) => {
          const data = customerDoc.data();

          customerList.push({
            _id: customerDoc.id,
            name: data.name || "Kund saknas",
            email: data.email || "",
            organizationNumber: data.organizationNumber || "",
            contactPerson: data.contactPerson || "",
            customerType: data.customerType || "",
          });
        });

        customerList.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setCustomers(customerList);
        setLoading(false);
      },
      (error) => {
        console.error("Fel vid hämtning av kunder:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [companyAccountUid, activeCompany?.companyName]);

  // Filtrera kunder baserat på sökning
  const filteredCustomers = useMemo(() => {
    return customers.filter((customer) => {
      const searchMatch =
        !searchTerm ||
        (customer.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (customer.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
        (customer.organizationNumber || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (customer.contactPerson || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (CUSTOMER_TYPE_LABELS_SV[customer.customerType] || customer.customerType || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());

      return searchMatch;
    });
  }, [customers, searchTerm]);

  // Hantera ändringar i ny kund-formuläret
  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomer((prev) => ({
      ...prev,
      [name]: value,
    }));
    setSavingError("");
  };

  // Hantera ändringar i redigeringsformuläret
  const handleEditCustomerChange = (e) => {
    const { name, value } = e.target;
    setSelectedCustomer((prev) => ({
      ...prev,
      [name]: value,
    }));
    setSavingError("");
  };

  // Spara ny kund
  const handleSaveCustomer = async () => {
    try {
      setSavingError("");

      if (!companyAccountUid) {
        setSavingError("Inget företagskonto hittades.");
        return;
      }

      if (!newCustomer.name.trim()) {
        setSavingError("Namn / företagsnamn måste fyllas i.");
        return;
      }

      // Email är inte längre obligatorisk - validering borttagen

      if (!newCustomer.customerType) {
        setSavingError("Välj kundtyp.");
        return;
      }

      const newCustomerRef = doc(collection(db, "users", companyAccountUid, "customers"));

      await setDoc(newCustomerRef, {
        name: newCustomer.name.trim(),
        email: newCustomer.email.trim() || "", // Tillåt tom email
        organizationNumber: newCustomer.organizationNumber.trim(),
        contactPerson: newCustomer.contactPerson.trim(),
        customerType: newCustomer.customerType,
        createdAt: new Date(),
        companyId: activeCompany?.companyId || "",
      });

      setShowAddPopup(false);
      setNewCustomer({
        name: "",
        email: "",
        organizationNumber: "",
        contactPerson: "",
        customerType: "",
      });
    } catch (error) {
      console.error("Fel vid sparande av kund:", error);
      setSavingError(`Det gick inte att spara kunden: ${error.message}`);
    }
  };

  // Uppdatera befintlig kund
  const handleUpdateCustomer = async () => {
    try {
      setSavingError("");

      if (!companyAccountUid || !selectedCustomer?._id) {
        setSavingError("Kundinformation saknas.");
        return;
      }

      if (!selectedCustomer.name.trim()) {
        setSavingError("Namn / företagsnamn måste fyllas i.");
        return;
      }

      // Email är inte längre obligatorisk - validering borttagen

      if (!selectedCustomer.customerType) {
        setSavingError("Välj kundtyp.");
        return;
      }

      const customerRef = doc(db, "users", companyAccountUid, "customers", selectedCustomer._id);

      await updateDoc(customerRef, {
        name: selectedCustomer.name.trim(),
        email: selectedCustomer.email?.trim() || "", // Tillåt tom email
        organizationNumber: selectedCustomer.organizationNumber?.trim() || "",
        contactPerson: selectedCustomer.contactPerson?.trim() || "",
        customerType: selectedCustomer.customerType,
        updatedAt: new Date(),
      });

      setShowEditPopup(false);
      setSelectedCustomer(null);
    } catch (error) {
      console.error("Fel vid uppdatering av kund:", error);
      setSavingError(`Det gick inte att uppdatera kunden: ${error.message}`);
    }
  };

  // Ta bort kund
  const handleDeleteCustomer = async (customerId) => {
    try {
      if (!companyAccountUid || !customerId) return;

      const customerRef = doc(db, "users", companyAccountUid, "customers", customerId);
      await deleteDoc(customerRef);
      
      setDeleteConfirm(null);
      if (showEditPopup) {
        setShowEditPopup(false);
        setSelectedCustomer(null);
      }
    } catch (error) {
      console.error("Fel vid borttagning av kund:", error);
      alert("Det gick inte att ta bort kunden. Försök igen.");
    }
  };

  // Öppna redigeringspopup för en kund
  const handleEditClick = (customer) => {
    setSelectedCustomer(customer);
    setShowEditPopup(true);
    setDeleteConfirm(null);
  };

  // Skapa en array med månader för select-listan
  const months = [
    "Januari", "Februari", "Mars", "April", "Maj", "Juni",
    "Juli", "Augusti", "September", "Oktober", "November", "December"
  ];

  // Skapa en array med år (nuvarande år och några år bakåt)
  const currentYear = parseInt(getCurrentYear());
  const years = [currentYear, currentYear - 1, currentYear - 2];

  // Visa laddningsindikator medan företag laddas
  if (companyLoading || loading) {
    return <LoadingIndicator message="Laddar kunder..." fullPage />;
  }

  // Visa meddelande om inget företag är valt
  if (!activeCompany) {
    return (
      <div style={{ padding: "40px", textAlign: "center", color: "#6b7280" }}>
        <p>Inget företag valt. Vänligen logga in igen.</p>
        <button
          onClick={() => window.location.href = "/login"}
          style={{
            padding: "10px 20px",
            background: "#6323ef",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            marginTop: 16,
          }}
        >
          Gå till inloggning
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: "0 0 24px" }}>
      {/* Visa aktivt företag */}
      <div style={{ marginBottom: 16, padding: "0 0 8px" }}>
        <p style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>
          Visar kunder för: <strong>{activeCompany?.companyName}</strong>
        </p>
      </div>

      {/* Verktygsrad med filter och knappar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 0 20px",
          overflowX: "auto",
          overflowY: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          flexWrap: "nowrap",
        }}
      >
        {/* Sökfält */}
        <div style={{ ...boxStyle, minWidth: 320, maxWidth: 420, flex: "1 1 360px" }}>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Sök på namn, email, organisationsnummer eller kontaktperson..."
            style={inputStyle}
          />
        </div>

        {/* År-filter */}
        <div style={boxStyle}>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {years.map(year => (
              <option key={year} value={year.toString()}>{year}</option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>År</span>
        </div>

        {/* Månad-filter */}
        <div style={boxStyle}>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            style={{
              ...inputStyle,
              appearance: "none",
              MozAppearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {months.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <FaChevronDown
            style={{
              position: "absolute",
              right: 10,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
              fontSize: 12,
              color: "#6b7280",
            }}
          />
          <span style={labelStyle}>Månad</span>
        </div>

        {/* Lägg till kund-knapp */}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <button
            onClick={() => setShowAddPopup(true)}
            style={{
              ...toolbarButtonStyle,
              background: "#3b82f6",
              border: "1px solid #2563eb",
            }}
          >
            <FaPlus />
          </button>
          <button
            type="button"
            onClick={() => navigate("/projects")}
            style={{
              ...toolbarButtonStyle,
              width: "auto",
              padding: "0 14px",
              fontSize: 14,
              fontWeight: 600,
              whiteSpace: "nowrap",
              background: "#3b82f6",
              border: "1px solid #2563eb",
            }}
          >
            Gå till projekt
          </button>
        </div>
      </div>

      {/* Rubrik med antal kunder */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          padding: "16px 18px",
          marginBottom: 14,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: 22,
            color: "#111827",
          }}
        >
          Kunder {!loading && `(${filteredCustomers.length})`}
        </h1>
      </div>

      {/* Kundtabell */}
      <div
        style={{
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          overflow: "hidden",
        }}
      >
        {/* Tabellhuvud */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1.2fr 1.8fr 1.2fr 1.2fr 1fr auto",
            gap: 12,
            padding: "14px 16px",
            borderBottom: "1px solid #e5e7eb",
            background: "#f9fafb",
          }}
        >
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>NAMN</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>EMAIL</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            ORG.NUMMER/PERSONNR
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            KONTAKTPERSON
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
            KUNDTYP
          </div>
          <div></div>
        </div>

        {/* Tabellrader */}
        {loading ? (
          <LoadingIndicator message="Laddar kunder..." />
        ) : filteredCustomers.length > 0 ? (
          filteredCustomers.map((customer) => {
            return (
              <div
                key={customer._id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.8fr 1.2fr 1.2fr 1fr auto",
                  gap: 12,
                  alignItems: "center",
                  padding: "14px 16px",
                  borderBottom: "1px solid #f1f5f9",
                  background: "#ffffff",
                  borderLeft: "4px solid transparent",
                }}
              >
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: "#111827",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {customer.name}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {customer.email}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {customer.organizationNumber}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {customer.contactPerson}
                </div>

                <div
                  style={{
                    fontSize: 14,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {CUSTOMER_TYPE_LABELS_SV[customer.customerType] || customer.customerType}
                </div>

                <div style={{ textAlign: "right" }}>
                  <button
                    onClick={() => handleEditClick(customer)}
                    style={{
                      background: "none",
                      border: "1px solid #d1d5db",
                      borderRadius: 8,
                      width: 38,
                      height: 38,
                      cursor: "pointer",
                      fontSize: 18,
                      color: "#374151",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    ⋮
                  </button>
                </div>
              </div>
            );
          })
        ) : (
          <div
            style={{
              padding: 24,
              textAlign: "center",
              color: "#6b7280",
              fontWeight: 500,
            }}
          >
            Inga kunder hittades.
          </div>
        )}
      </div>

      {/* Lägg till kund-popup */}
      {showAddPopup && (
        <div
          onClick={() => {
            setShowAddPopup(false);
            setSavingError("");
            setNewCustomer({
              name: "",
              email: "",
              organizationNumber: "",
              contactPerson: "",
              customerType: "",
            });
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 520,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 22,
                  color: "#111827",
                }}
              >
                Lägg till kund
              </h3>

              <button
                onClick={() => {
                  setShowAddPopup(false);
                  setSavingError("");
                  setNewCustomer({
                    name: "",
                    email: "",
                    organizationNumber: "",
                    contactPerson: "",
                    customerType: "",
                  });
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 20,
                  color: "#6b7280",
                }}
              >
                <FaTimes />
              </button>
            </div>

            {savingError && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  padding: "10px",
                  borderRadius: "6px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {savingError}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <input
                name="name"
                value={newCustomer.name}
                onChange={handleNewCustomerChange}
                placeholder="Namn / Företagsnamn *"
                style={inputStyle}
              />
              <input
                name="email"
                value={newCustomer.email}
                onChange={handleNewCustomerChange}
                placeholder="Email"
                style={inputStyle}
              />
              <input
                name="organizationNumber"
                value={newCustomer.organizationNumber}
                onChange={handleNewCustomerChange}
                placeholder="Organisationsnummer / Personnummer"
                style={inputStyle}
              />
              <input
                name="contactPerson"
                value={newCustomer.contactPerson}
                onChange={handleNewCustomerChange}
                placeholder="Kontaktperson"
                style={inputStyle}
              />

              <select
                name="customerType"
                value={newCustomer.customerType}
                onChange={handleNewCustomerChange}
                style={{ ...inputStyle, paddingRight: 12 }}
              >
                <option value="">Välj kundtyp *</option>
                <option value="company">Företag</option>
                <option value="organization">Organisation</option>
                <option value="individual">Privatperson</option>
              </select>
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              * Namn och Kundtyp är obligatoriska fält
            </div>

            <div
              style={{
                display: "flex",
                gap: 10,
                marginTop: 18,
              }}
            >
              <button
                onClick={handleSaveCustomer}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "none",
                  background: "#2563eb",
                  color: "#fff",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Spara
              </button>

              <button
                onClick={() => {
                  setShowAddPopup(false);
                  setSavingError("");
                  setNewCustomer({
                    name: "",
                    email: "",
                    organizationNumber: "",
                    contactPerson: "",
                    customerType: "",
                  });
                }}
                style={{
                  height: 42,
                  padding: "0 16px",
                  borderRadius: 10,
                  border: "1px solid #d1d5db",
                  background: "#fff",
                  color: "#111827",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Avbryt
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Redigera kund-popup */}
      {showEditPopup && selectedCustomer && (
        <div
          onClick={() => {
            setShowEditPopup(false);
            setSelectedCustomer(null);
            setDeleteConfirm(null);
          }}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 3000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: 24,
              width: 520,
              maxWidth: "90vw",
              boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
              border: "1px solid #e5e7eb",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 18,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 22,
                  color: "#111827",
                }}
              >
                Redigera kund
              </h3>

              <button
                onClick={() => {
                  setShowEditPopup(false);
                  setSelectedCustomer(null);
                  setDeleteConfirm(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 20,
                  color: "#6b7280",
                }}
              >
                <FaTimes />
              </button>
            </div>

            {savingError && (
              <div
                style={{
                  backgroundColor: "#fee2e2",
                  color: "#991b1b",
                  padding: "10px",
                  borderRadius: "6px",
                  marginBottom: "16px",
                  fontSize: "14px",
                }}
              >
                {savingError}
              </div>
            )}

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 14,
              }}
            >
              <input
                name="name"
                value={selectedCustomer.name}
                onChange={handleEditCustomerChange}
                placeholder="Namn / Företagsnamn *"
                style={inputStyle}
              />
              <input
                name="email"
                value={selectedCustomer.email}
                onChange={handleEditCustomerChange}
                placeholder="Email"
                style={inputStyle}
              />
              <input
                name="organizationNumber"
                value={selectedCustomer.organizationNumber}
                onChange={handleEditCustomerChange}
                placeholder="Organisationsnummer / Personnummer"
                style={inputStyle}
              />
              <input
                name="contactPerson"
                value={selectedCustomer.contactPerson}
                onChange={handleEditCustomerChange}
                placeholder="Kontaktperson"
                style={inputStyle}
              />

              <select
                name="customerType"
                value={selectedCustomer.customerType}
                onChange={handleEditCustomerChange}
                style={{ ...inputStyle, paddingRight: 12 }}
              >
                <option value="">Välj kundtyp *</option>
                <option value="company">Företag</option>
                <option value="organization">Organisation</option>
                <option value="individual">Privatperson</option>
              </select>
            </div>

            <div
              style={{
                fontSize: 12,
                color: "#6b7280",
                marginTop: 8,
                marginBottom: 8,
              }}
            >
              * Namn och Kundtyp är obligatoriska fält
            </div>

            {deleteConfirm === selectedCustomer._id ? (
              <div
                style={{
                  marginTop: 18,
                  padding: 16,
                  background: "#fee2e2",
                  borderRadius: 8,
                  border: "1px solid #fecaca",
                }}
              >
                <p style={{ margin: "0 0 12px 0", color: "#991b1b", fontWeight: 500 }}>
                  Är du säker på att du vill ta bort denna kund?
                </p>
                <div style={{ display: "flex", gap: 10 }}>
                  <button
                    onClick={() => handleDeleteCustomer(selectedCustomer._id)}
                    style={{
                      height: 38,
                      padding: "0 16px",
                      borderRadius: 8,
                      border: "none",
                      background: "#dc2626",
                      color: "#fff",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Ja, ta bort
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    style={{
                      height: 38,
                      padding: "0 16px",
                      borderRadius: 8,
                      border: "1px solid #9ca3af",
                      background: "#fff",
                      color: "#374151",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    Avbryt
                  </button>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  marginTop: 18,
                }}
              >
                <button
                  onClick={handleUpdateCustomer}
                  style={{
                    height: 42,
                    padding: "0 16px",
                    borderRadius: 10,
                    border: "none",
                    background: "#2563eb",
                    color: "#fff",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Spara ändringar
                </button>

                <button
                  onClick={() => {
                    setShowEditPopup(false);
                    setSelectedCustomer(null);
                    setDeleteConfirm(null);
                  }}
                  style={{
                    height: 42,
                    padding: "0 16px",
                    borderRadius: 10,
                    border: "1px solid #d1d5db",
                    background: "#fff",
                    color: "#111827",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Avbryt
                </button>

                <button
                  onClick={() => setDeleteConfirm(selectedCustomer._id)}
                  style={{
                    height: 42,
                    padding: "0 16px",
                    borderRadius: 10,
                    border: "1px solid #fecaca",
                    background: "#fee2e2",
                    color: "#dc2626",
                    fontWeight: 600,
                    cursor: "pointer",
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <FaTrash /> Ta bort
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
